import React, { useState, useEffect, useRef } from 'react'

// Deps
import { connect } from 'react-redux'
import parseISO from 'date-fns/parseISO'
import format from 'date-fns/format';
import compareAsc from 'date-fns/compareAsc'
import tr from 'date-fns/locale/tr'
import XLSX from 'xlsx';

// Partials
import { Table, TR, TD } from 'views/shared/partials/table'
import Loader from 'views/shared/partials/loader'
import Pricebox from 'views/shared/partials/pricebox'
import OrderStatus from 'views/shared/partials/order-status'
import CommerceOrderStatus from 'views/shared/partials/commerce-order-status'
import Btn from 'views/shared/partials/btn'
import Dropdown from 'views/shared/partials/dropdown'
import Link from 'views/shared/partials/link'
import { InputForm, FormInput } from 'views/shared/partials/forms'

// Functions
import formatDate from 'functions/format-date'
// import getCurrencySymbol from 'functions/get-currency-symbol'
// import isExact from 'functions/is-exact'

// Utils
import { openModal } from 'utils/modals';

// Controllers
import { setError } from 'controllers/navigator';

// Services
import orderServices from 'services/order'
import companyServices from 'services/company'

// Hooks
import useGroups from 'hooks/groups'
import useSelectQuery from 'hooks/select-query'

// Data
import orderStatusCodes from 'data/order-status-codes'
import orderProductStatusCodes from 'data/commerce-status-codes-commerce'
import paymentOrderTypes from 'data/payment-order-types'
import rawCurrencyTypes from 'data/currency-types'
import deliveryMethods from 'data/delivery-methods'

const defaultCurrencyType = { value: '0', label: 'Tümü' }
const defaultClient = { value: '', label: 'Tüm Müşteriler', company_name: '' }
const defaultAccount = { value: '', label: 'Tüm Kullanıcılar', company_name: '' }
const currencyTypes = [ defaultCurrencyType, ...rawCurrencyTypes ]

const getDefaultSelectQuery = () => {
	return  {
		'status_code': orderStatusCodes[0],
		'product_status': orderProductStatusCodes[0],
		'sort_by': paymentOrderTypes[0],
		'store_code': {value: '', label: 'Tüm Gruplar'},
		'company_account_code': defaultAccount,
		'client_code': defaultClient,
		'product_delivery_method_id': deliveryMethods[0],
	}
}

const calculateSelectValue = (value, key, groups) => {
	return new Promise((resolve) => {
		if(key === 'status_code') {
			resolve({ key: key, value: orderStatusCodes.find((opt) => {
				return opt.value.toString() === value.toString()
			})});
		}

		else if(key === 'product_status') {
			resolve({ key: key, value: orderProductStatusCodes.find((opt) => {
				return opt.value.toString() === value.toString()
			})});
		}

		else if(key === 'store_code') {
			resolve({ key: key, value: groups.find((opt) => {
				return opt.value.toString() === value.toString()
			})});
		}

		else if(key === 'sort_by') {
			resolve({ key: key, value: paymentOrderTypes.find((opt) => {
				return opt.value.toString() === value.toString()
			})});
		}

		else if(key === 'price_type_id') {
			resolve({ key: key, value: [defaultCurrencyType, ...currencyTypes].find((opt) => {
				return opt.value.toString() === value.toString()
			})});
		}

		else if(key === 'company_account_code') {
			if(value) {
				companyServices.getAccount(value).then((account) => {
					resolve({ key: key, value: {...account, value: account.company_account_code, label: (account.name + ' ' + account.surname)} })
				}).catch(() => {
					resolve(false);
				})
			}
			else {
				resolve(false);
			}

		}

		else if(key === 'client_code') {
			if(value) {
				companyServices.getClient(value).then((client) => {
					resolve({ key: key, value: {...client, value: client.client_code, label: (client.company_name ? (client.company_name + (client.executive_name ? ' / ' : '')) : '') + client.executive_name + ' ' + client.executive_surname}})
				}).catch(() => {
					resolve(false);
				})
			}
			else {
				resolve(false);
			}

		}

		else if(key === 'product_delivery_method_id') {
			resolve({ key: key, value: deliveryMethods.find((opt) => {
				return opt.value.toString() === value.toString()
			})});
		}

		else {
			resolve(false);
		}
	});
}

const searchAccounts = (search) => {
	return new Promise((resolve, reject) => {
		companyServices.listAccounts({ search_value: search }).then((payload) => {
			let results = payload.accounts.map((account) => {
				return {...account, value: account.company_account_code, label: (account.name + ' ' + account.surname)};
			})

			if(search === '') {
				results = [
					defaultAccount,
					...results
				];
			}
			resolve(results);
		}).catch(() => {
			reject(false);
		})
	})
}

const searchClients = (search) => {
	return new Promise((resolve, reject) => {
		companyServices.listClients({ search_value: search }).then((payload) => {
			let results = payload.clients.map((client) => {
				return {...client, value: client.client_code, label: (client.company_name ? (client.company_name + (client.executive_name ? ' / ' : '')) : '') + client.executive_name + ' ' + client.executive_surname};
			})

			if(search === '') {
				results = [
					defaultClient,
					...results
				];
			}
			resolve(results);
		}).catch(() => {
			reject(false);
		})
	})
}

const mapStateToProps = (state, ownProps) => {
	return {
		userGroups: state.user.groups,
		userData: state.user.data,
	};
};

const OrderList = (props) => {
	//const rawQuery = props.query;
	const query = props.query;

	const plain = props.orderPlanID ? true : props.plain;

	const searchForm = useRef();

	const [data, setData] = useState(false);
	const [showFilters, setShowFilters] = useState(props.initialFiltersOpen === true /*(rawQuery && rawQuery.length) ? true : false*/);
	const [excelLoading, setExcelLoading] = useState(false);

	if(props.update) {
		props.update(() => { makeSearch(query); })
	}

	const groups = useGroups(props.userGroups);
	const selectQuery = useSelectQuery(query, groups, calculateSelectValue, getDefaultSelectQuery);

	const [paymentStartDate, setPaymentStartDate] = useState(query.start_date ? parseISO(query.start_date.split(' ')[0]) : false);
	const [paymentDueDate, setPaymentDueDate] = useState(query.due_date ? parseISO(query.due_date.split(' ')[0]) : false);

	const makeSearch = (query) => {
		setData(false);

		setPaymentStartDate(query.start_date ? parseISO(query.start_date.split(' ')[0]) : false);
		setPaymentDueDate(query.due_date ? parseISO(query.due_date.split(' ')[0]) : false);
		if(searchForm.current) {
			searchForm.current.reset();
		}

		orderServices.listOrders(query).then((payload) => {
			setData(payload);
		}).catch(() => {
			setError();
		})
	};

	const getExcel = () => {
		setExcelLoading(true);
		let excelQuery = {...query, page: 1, per_page: 1000};

		orderServices.listOrders(excelQuery).then((payload) => {
			let ws = XLSX.utils.json_to_sheet(payload.orders.map(order => ({
				...order,
				account: order.account?.company_account_code,
				group: order.group?.name,
				target_group: order.target_group?.name,
				price_type: order.price_type?.currency_code,
				client: order.client ? order.client.executive_name + ' ' + order.client.executive_surname + (order.client.company_name ? `(${order.client.company_name})`: '') : '-'
			})));
			let wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "Döküm");

			XLSX.writeFile(wb, format(new Date(), "d MMMM yyyy - HH-mm-ss", { locale: tr }) + " " + props.userData.company.name + " Ödemeler Raporu.xlsx");
		}).catch(() => {
			setError();
		}).finally(() => {
			setExcelLoading(false);
		})
	}

	useEffect(() => {
		if(paymentStartDate && paymentDueDate) {
			if(compareAsc(paymentStartDate, paymentDueDate) === 1) {
				setPaymentDueDate(false);
			}
		}
	}, [paymentStartDate, paymentDueDate])

	useEffect(() => {
		makeSearch(query);
	}, [query])

	return (
		<section className={'portlet section orderlist ' + (props.className ? props.className : '')}>
			<div className="portlet-header">
				<strong className="portlet-title">{props.title}</strong>

				{plain ?
					<React.Fragment>
						{!props.orderPlanID &&
							<div className="header-controls">
								<Btn white small wide tag="link" href="payments">Tüm İşlemler</Btn>
							</div>
						}
					</React.Fragment>
					:
					<div style={{display: 'none'}}>
						{props.clientID ?
							<div className="header-controls">
								<Btn white small wide icon="plus" onClick={() => { openModal('create-order', {clientID: props.clientID}); }}>Yeni Talep Oluştur</Btn>
							</div>
							:
							<div className="header-controls">
								<Btn white small wide icon="plus" onClick={() => { openModal('create-order'); }}>Yeni Talep Oluştur</Btn>
							</div>
						}
					</div>
				}
			</div>

			{!plain &&
				<InputForm
					ignoreEmpty
					className="portlet-filters"
					ref={searchForm}
					onSubmit={props.onSearch}>
					<div className="filters-searchwrap">
						<FormInput
							className="lower"
							type="text"
							name="search_value"
							value={query.search_value}
							//onChange={(val) => { setFilters({...filters, search_value: val}) }}
							placeholder="Müşteri Adı, Müşteri Kodu, Müşteri ERP Kodu vs...">
						</FormInput>
						<button
							type="submit"
							className="searchwrap-submit">
							<i className="icon-search"></i> ara
						</button>
					</div>
					<Btn
						white small wide
						type="button"
						onClick={() => { setShowFilters(!showFilters)}}
						className="filters-expandbtn"
						icon="filter">
						Detaylı Arama
					</Btn>

					<div className={'filters-more grid-container' + (showFilters ? ' show' : '')}>

						<div className="row">
							<div className="col x2">
								<FormInput
									z="8"
									type="select"
									label="Talep Durumu"
									options={orderStatusCodes}
									defaultValue={selectQuery['status_code'] ? selectQuery['status_code'] : orderStatusCodes[0]}
									name="status_code">
								</FormInput>
							</div>
							<div className="col x2">
								<FormInput
									type="select"
									isSearchable
									z="7"
									defaultValue={selectQuery['store_code'] ? selectQuery['store_code'] : groups[0]}
									label="Grup"
									options={groups}
									name="store_code" />
							</div>
							<div className="col x2">
								<FormInput
									type="select"
									z="6"
									label="Kullanıcı"
									async
									defaultValue={selectQuery['company_account_code'] ? selectQuery['company_account_code'] : defaultAccount}
									defaultOptions
									loadOptions={searchAccounts}
									name="company_account_code" />
							</div>
							<div className="col x2">
								<FormInput
									type="select"
									z="5"
									disabled={props.clientID}
									label="Müşteri"
									async
									defaultValue={selectQuery['client_code'] ? selectQuery['client_code'] : defaultClient}
									defaultOptions
									loadOptions={searchClients}
									name="client_code" />
							</div>
							<div className="col x2">
								<FormInput
									type="select"
									z="4"
									label="Sipariş Durumu"
									defaultValue={selectQuery['product_status'] ? selectQuery['product_status'] : orderProductStatusCodes[0]}
									options={orderProductStatusCodes}
									name="product_status" />
							</div>
							<div className="col x2">
								<FormInput
									type="select"
									z="3"
									label="Teslimat"
									defaultValue={selectQuery['product_delivery_method_id'] ? selectQuery['product_delivery_method_id'] : deliveryMethods[0]}
									options={deliveryMethods}
									name="product_delivery_method_id" />
							</div>
						</div>
						<div className="row">
							<div className="col x3">
								<FormInput
									z="2"
									type="select"
									label="Para Birimi"
									options={currencyTypes}
									defaultValue={selectQuery['price_type_id'] ? selectQuery['price_type_id'] : defaultCurrencyType}
									name="price_type_id" />
							</div>
							<div className="col x6">
								<FormInput
									type="date"
									z="1"
									range
									label="Tarih Aralığı"
									placeholder="Tarih Aralığı Seçin"
									onChange={(value) => { setPaymentStartDate(value[0]); setPaymentDueDate(value[1]); }}
									value={((paymentStartDate && paymentDueDate) ? [paymentStartDate, paymentDueDate] : undefined)}
									dateFormat="yyyy-MM-dd 00:00:00"
									endDateFormat="yyyy-MM-dd 23:59:59"
									startDateName="start_date"
									endDateName="due_date" />
							</div>
							{/*<div className="col x3">
								<FormInput
									type="date"
									z="1"
									label="Bitiş Tarihi"
									placeholder="Bitiş Tarihi Seçin"
									onChange={(value) => { setPaymentDueDate(value); }}
									minDate={paymentStartDate ? paymentStartDate : undefined}
									value={paymentDueDate}
									dateFormat="yyyy-MM-dd 23:59:59"
									name="due_date" />
							</div>*/}
							<div className="col x3">
								<FormInput
									z="1"
									type="select"
									label="Sıralama"
									options={paymentOrderTypes}
									defaultValue={selectQuery['sort_by'] ? selectQuery['sort_by'] : paymentOrderTypes[0]}
									name="sort_by" />
							</div>
						</div>
						<div className="row">
							<div className="col more-controls">
								<Btn
									small wide white
									disabled={excelLoading}
									loading={excelLoading}
									type="submit"
									onClick={getExcel}
									icon="excel">
								</Btn>
								<Btn
									small wide white
									type="submit"
									icon="search">
									Sonuçları Getir
								</Btn>
							</div>
						</div>

					</div>
				</InputForm>
			}
			<div className="portlet-section bottom loader-container">
				<Loader loading={!data} />

				{data &&
					<Table
						emptyMessage="Hiç ödeme bulunmuyor."
						className="list-table"
						pagination={!plain ? data.pagination : undefined}
						onPaginate={(page) => { props.onPaginate(page); }}
						heading={
							<React.Fragment>
								<th>Müşteri</th>
								<th>Grup / Talep Kodu</th>
								<th>Talep Tutarı</th>
								<th>Kalan Bakiye</th>
								<th>Ödeme Durumu</th>
								<th>Sipariş Durumu</th>
								<th>Oluşturulma Tarihi</th>
								<th></th>
							</React.Fragment>
						}>

						{data.orders.map((order, nth) => (
							<TR key={nth} mobileTitle={'#' + order.business_order_code}>
								<TD mobileTitle="Müşteri">
									{order.client.company_name ?
										<>
											<p>
												<Link href="commerceOrderDetail" params={{ id: order.business_order_code }}>
													<strong>{order.client.company_name}</strong><br />
													{order.client.executive_name + ' ' + order.client.executive_surname}
												</Link>
											</p>
										</>
										:
										<p>
											<Link href="commerceOrderDetail" params={{ id: order.business_order_code }}>
												<strong>
													{order.client.executive_name + ' ' +order.client.executive_surname}
												</strong>
											</Link>
										</p>
									}
									<p className="table-subtitle">
										<Link
											href="commerceOrderDetail"
											params={{ id: order.business_order_code }}>
											{order.client.client_code}
										</Link>
									</p>
								</TD>
								<TD mobileTitle="Grup">
									<p>
										<Link
											href="commerceOrderDetail"
											params={{ id: order.business_order_code }}>
											<strong>{order.group.name}</strong>
										</Link>
									</p>
									<p className="table-subtitle hide-mobile">
										<Link
											href="commerceOrderDetail"
										params={{ id: order.business_order_code }}>
											{order.business_order_code}
										</Link>
									</p>
								</TD>
								<TD mobileTitle="Talep Tutarı">
									<p>
										<Link
											href="commerceOrderDetail"
											params={{ id: order.business_order_code }}>
											<Pricebox
												currency={order.price_type.currency_symbol}
												price={order.total_amount} />
										</Link>
									</p>
									{(order.currency_exchange_amount && parseFloat(order.currency_exchange_amount) > 0) &&
										<p>
											<Link
												href="commerceOrderDetail"
												params={{ id: order.business_order_code }}>
												<span className="label dark text-regular">
													<Pricebox
														monochrome
														price={order.currency_exchange_amount} />
												</span>
											</Link>
										</p>
									}
								</TD>
								<TD mobileTitle="Kalan Tutar">
									<Link
										href="commerceOrderDetail"
										params={{ id: order.business_order_code }}>
										<Pricebox
											warning
											currency={order.price_type.currency_symbol}
											price={parseFloat(order.total_amount) - (order.payment_amount ? parseFloat(order.payment_amount) : 0) + (order.refund_amount ? parseFloat(order.refund_amount) : 0)} />
									</Link>
								</TD>
								<TD>
									<Link
										href="commerceOrderDetail"
										params={{ id: order.business_order_code }}>
										<OrderStatus status={order.status} />
									</Link>
								</TD>
								<TD>
									<Link
										href="commerceOrderDetail"
										params={{ id: order.business_order_code }}>
										<CommerceOrderStatus status={order.product_order_status} />
									</Link>
								</TD>
								<TD className="mobile-full">
									<p className="table-text-light">
										<Link
											href="commerceOrderDetail"
											params={{ id: order.business_order_code }}>
											<strong>{formatDate(order.created_at)}</strong>
										</Link>
									</p>
									{order.due_date &&
										<p className="table-subtitle">
											<Link
												href="commerceOrderDetail"
												params={{ id: order.business_order_code }}>
											</Link>
										</p>
									}
								</TD>
								<TD className="controls">
									<Dropdown
										text="İşlemler"
										icon="list">
										<Link
											href="commerceOrderDetail"
											params={{ id: order.business_order_code }}>
											Detaya Git
										</Link>
										<button
											className="blue"
											onClick={() => {openModal('order-summary', { orderCode: order.business_order_code }) }}>
											Link Gönder
										</button>
									</Dropdown>
								</TD>
							</TR>
						))}
					</Table>
				}
			</div>
		</section>
	)
}

OrderList.defaultProps = {
	query: {},
	plain: false,
	orderPlanID: false,
	initialFiltersOpen: false,
	onPaginate: () => {},
	onSearch: () => {},
	title: 'Siparişler',
	clientID: false,
}

export default connect(mapStateToProps)(OrderList);
